import Autocomplete, {
  AutocompleteCloseReason
} from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import CancelIcon from '@mui/icons-material/Cancel'
import {
  primaryColors,
  whiteShades
} from '@assets/styles/helpers/colors.styles'
import { Checkbox, Chip, ListItem, Typography } from '@mui/material'
import { selectValueType } from '@/shared/components/DynamicForm/DynamicForm'
import { OptionType } from '@/shared/api/BaseAPI.model'
import {
  HTMLAttributes,
  ReactNode,
  SyntheticEvent,
  forwardRef,
  useState
} from 'react'
import {
  chipStyles,
  keepCaseStyles,
  listboxStyles,
  listElementStyles,
  popperStyles,
  selectStyles
} from './Select.styles'
import { inputStyles } from '../TextField/TextField.styles'
import styles from './Select.module.scss'

interface SelectProps {
  field?: {
    name: string
  }
  label?: string
  placeholder?: string
  options: OptionType[]
  hasError?: boolean
  helperText?: string
  disabled?: boolean
  focused?: boolean
  isMutiSelect?: boolean
  flexGrow?: number
  onBlur?: () => void
  value?: selectValueType
  hideClearIcon?: boolean
  widthFitContent?: boolean
  onChange?: (value: selectValueType) => void
  defaultValue?: selectValueType
  onOpen?: () => void
  /** Says why, so a list that closes on a pick can be told from one dismissed. */
  onClose?: (event: SyntheticEvent, reason: AutocompleteCloseReason) => void
  minWidth?: string
  maxWidth?: string
  readonly?: boolean
  required?: boolean
  /**
   * Values that must stay selected. Their chips lose the delete icon, their
   * options cannot be toggled off, and anything that clears the field wholesale
   * (the clear icon, backspace) puts them straight back.
   */
  lockedValues?: string[]
  /**
   * Keeps every visible chip whole in a narrow field. They stay on one row and
   * give way to each other, letting the label ellipsis do the shortening, so
   * none is left sliced in half by the field's edge.
   */
  fitTags?: boolean
  /**
   * Draws an option as something other than its text: a specimen of a typeface,
   * a swatch. The plain label is used wherever this is absent, which is
   * everywhere but the font pickers.
   */
  renderOptionLabel?: (option: OptionType) => ReactNode
  /**
   * Hands the typed text to the owner and stops the list filtering itself. For
   * a list too long to hand over whole, where the caller narrows it and decides
   * what the visible options cost, as the font pickers do with 1315 families.
   */
  onSearch?: (value: string) => void
  /**
   * The text in the field, held by the owner. A search box keeps what was
   * typed after a pick, where a picker would put the picked label there.
   */
  inputValue?: string
  /** Opens the list from outside, e.g. only once there is something to show. */
  open?: boolean
  /** The list is on its way; `loadingText` stands in while it is empty. */
  loading?: boolean
  loadingText?: ReactNode
  noOptionsText?: ReactNode
  inputMaxLength?: number
  /** A search box has nothing to drop down before something is typed. */
  hidePopupIcon?: boolean
  /**
   * Leaves the text as written. Choices are capitalised everywhere else, which
   * turned a typed address into "Ul. Lipowa" and the placeholder into Title
   * Case.
   */
  keepCase?: boolean
}

type PlainOptionProps = HTMLAttributes<HTMLLIElement>

/*
  An option the owner draws itself. `ListItem` costs an emotion style per row,
  which is nothing for the twenty options a form has and is the difference
  between instant and sluggish for the eighteen hundred in the font pickers.
*/
function PlainOption({ children, className, ...rest }: PlainOptionProps) {
  return (
    <li {...rest} className={[className, styles.plainOption].join(' ')}>
      {children}
    </li>
  )
}

const Select = forwardRef<HTMLDivElement, SelectProps>((props, ref) => {
  const {
    label,
    placeholder,
    options,
    hasError,
    helperText,
    disabled,
    field,
    focused,
    isMutiSelect,
    flexGrow,
    value,
    onBlur,
    hideClearIcon,
    widthFitContent,
    onChange,
    defaultValue,
    onOpen,
    onClose,
    minWidth,
    maxWidth,
    readonly = false,
    required = false,
    lockedValues,
    fitTags,
    renderOptionLabel,
    onSearch = false,
    inputValue,
    open,
    loading,
    loadingText,
    noOptionsText,
    inputMaxLength,
    hidePopupIcon,
    keepCase
  } = props

  // Collapsing the chips to "first N + more" is normally MUI's `limitTags`,
  // but that only kicks in when renderTags returns an array, and the chips
  // need a wrapper element of their own to scroll in. So the same rule is
  // applied by hand below.
  const tagsLimit = isMutiSelect ? 2 : -1

  const hasLocked = !!lockedValues?.length
  const isLocked = (option: OptionType) =>
    hasLocked && lockedValues!.includes(option.value as string)
  const [isFocused, setIsFocused] = useState(false)

  // With `multiple`, the chips are separate elements and the text input stays
  // EMPTY behind them, so its placeholder keeps rendering next to a selection,
  // squeezed into whatever width the chips leave over ("Wybierz Użyt…"). Drop it
  // as soon as there is a value. Single-select is unaffected either way (the
  // input holds the label, so the browser hides the placeholder itself).
  const hasValue = Array.isArray(value) ? value.length > 0 : value != null

  return (
    <Autocomplete
      id={field?.name}
      onFocus={() => setIsFocused(true)}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          setIsFocused(false)
        }
      }}
      options={options}
      /*
        The owner has already narrowed the list, so filtering it again here
        would hide options it deliberately put in front of the reader.
      */
      filterOptions={onSearch ? optionList => optionList : undefined}
      onInputChange={(_, next, reason) => {
        if (onSearch && reason === 'input') onSearch(next)
      }}
      inputValue={inputValue}
      open={open}
      loading={loading}
      loadingText={loadingText}
      noOptionsText={noOptionsText}
      forcePopupIcon={hidePopupIcon ? false : 'auto'}
      size="small"
      // A field nobody types into has nothing to select, and highlighting its
      // label on every click reads as a glitch.
      selectOnFocus={!readonly}
      multiple={isMutiSelect}
      disableCloseOnSelect={isMutiSelect}
      renderOption={(props, option: OptionType, { selected }) =>
        renderOptionLabel ? (
          <PlainOption {...props}>{renderOptionLabel(option)}</PlainOption>
        ) : (
          <ListItem {...props} sx={listElementStyles}>
            {isMutiSelect && (
              <Checkbox style={{ marginRight: 8 }} checked={selected} />
            )}
            {option.iconImage && (
              <img
                className={styles.optionIconImage}
                src={option.iconImage}
                alt="option-icon"
              />
            )}
            <Typography className="body-bold-medium">{option.label}</Typography>
            {option.icon && <i className={option.icon} />}
          </ListItem>
        )
      }
      getOptionLabel={option => option.label || ''}
      // By value, which is what tells options apart here (see
      // isOptionEqualToValue). MUI keys rows by label otherwise, and two
      // branches of a chain share one.
      getOptionKey={option =>
        typeof option === 'string' ? option : String(option.value)
      }
      getOptionDisabled={hasLocked ? isLocked : undefined}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      value={value}
      defaultValue={defaultValue}
      disabled={disabled}
      onChange={(_, newValue) => {
        if (onChange) {
          if (isMutiSelect) {
            let nextValue = Array.isArray(newValue) ? [...newValue] : newValue

            // `getOptionDisabled` only guards the list. The clear icon and
            // backspace go straight through it, and clearing the field is
            // exactly the case a locked value exists to survive.
            if (hasLocked && Array.isArray(nextValue)) {
              const present = new Set(nextValue.map(option => option.value))
              // Put back from what was selected a moment ago, never from
              // `options`. A locked value is by definition already selected,
              // while the option list can still be loading or filtered, and
              // rebuilding from there let the lock fail silently in exactly the
              // state it exists to survive.
              const previous = Array.isArray(value) ? value : []
              const restored = previous.filter(
                option => isLocked(option) && !present.has(option.value)
              )
              nextValue = [...nextValue, ...restored]
            }

            const sortedNewValue = Array.isArray(nextValue)
              ? nextValue.sort((a, b) => a.label.localeCompare(b.label))
              : nextValue
            onChange(sortedNewValue)
          } else {
            onChange(newValue)
          }
        }
      }}
      renderInput={params => (
        <TextField
          {...params}
          label={required && label ? `${label} *` : label}
          placeholder={hasValue ? undefined : placeholder}
          variant="outlined"
          rows={1}
          inputProps={{
            ...params.inputProps,
            ...(inputMaxLength && { maxLength: inputMaxLength }),
            ...(readonly && {
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key.length === 1) e.preventDefault()
              },
              style: {
                ...((params.inputProps as unknown as Record<string, unknown>)
                  ?.style as object),
                caretColor: 'transparent',
                cursor: 'pointer'
              }
            })
          }}
          sx={{
            ...inputStyles,
            width: widthFitContent
              ? 'fit-content !important'
              : '100% !important',
            '.MuiInputBase-root.MuiOutlinedInput-root': {
              paddingRight: widthFitContent
                ? '1.75rem !important'
                : '4rem !important',
              maxHeight: '9rem',
              overflowY: 'auto',
              minWidth: minWidth || '100%',
              maxWidth: maxWidth || '100%'
            }
          }}
          helperText={helperText}
          error={hasError}
          onBlur={onBlur}
          focused={focused}
        />
      )}
      sx={{
        ...selectStyles,
        ...(keepCase && keepCaseStyles),
        flexGrow,
        '&.MuiAutocomplete-root': {
          width: widthFitContent ? 'fit-content' : '100%',
          maxWidth: maxWidth || '100%'
        }
      }}
      clearIcon={
        !hideClearIcon &&
        value && (
          <CloseOutlinedIcon
            sx={{ color: `${whiteShades.shade1} !important` }}
            fontSize="small"
          />
        )
      }
      popupIcon={<ExpandMoreIcon sx={{ color: primaryColors.white }} />}
      slotProps={{
        paper: {
          sx: keepCase
            ? { ...popperStyles, textTransform: 'none' }
            : popperStyles
        },
        listbox: {
          sx: listboxStyles
        }
      }}
      renderTags={(value, getTagProps) => {
        /*
          Focusing normally expands the strip to every chip, which is fine when
          it can scroll. A field that has asked not to clip has nowhere to put
          them, and they ended up fighting over one row, so there the collapse
          stays on and the dropdown's own ticks show the full selection.
        */
        const isCollapsed =
          tagsLimit > -1 && value.length > tagsLimit && (fitTags || !isFocused)
        const visibleTags = isCollapsed ? value.slice(0, tagsLimit) : value
        const hiddenCount = value.length - visibleTags.length

        return (
          <div
            className={`${styles.tagScroller} ${
              fitTags ? styles.tagScrollerFitted : ''
            }`}
            // Vertical wheel scrolls the strip sideways, without it the chips
            // beyond the right edge are unreachable on a mouse.
            onWheel={event => {
              const strip = event.currentTarget
              if (strip.scrollWidth > strip.clientWidth) {
                strip.scrollLeft += event.deltaY
              }
            }}
          >
            {visibleTags.map((option: OptionType, index) => {
              // Dropping `onDelete` is what removes the icon; MUI renders none
              // when the handler is absent.
              const { onDelete, ...tagProps } = getTagProps({ index })

              return (
                <Chip
                  {...tagProps}
                  {...(isLocked(option) ? {} : { onDelete })}
                  label={option.label}
                  sx={chipStyles}
                  deleteIcon={<CancelIcon fontSize="small" />}
                />
              )
            })}
            {hiddenCount > 0 && (
              <span className="MuiAutocomplete-tag">{`+${hiddenCount}`}</span>
            )}
          </div>
        )
      }}
      onOpen={onOpen}
      onClose={onClose}
      ref={ref}
    />
  )
})

export default Select
